
<template>
  <v-dialog
    :model-value="modelValue"
    max-width="560"
    persistent
    @update:model-value="emit('update:modelValue', $event)"
  >
    <v-card rounded="xl">
      <!-- =====================================================
           HEADER
      ====================================================== -->

      <v-card-title class="d-flex align-center pa-5">
        <v-avatar
          color="success"
          variant="tonal"
          size="44"
          class="mr-3"
        >
          <v-icon>mdi-play-circle-outline</v-icon>
        </v-avatar>

        <div>
          <div class="text-h6 font-weight-bold">
            Start POS Session
          </div>

          <div class="text-caption text-medium-emphasis">
            {{ terminalCode }}
          </div>
        </div>

        <v-spacer />

        <v-btn
          icon="mdi-close"
          variant="text"
          :disabled="loading"
          @click="close"
        />
      </v-card-title>

      <v-divider />

      <!-- =====================================================
           BODY
      ====================================================== -->

      <v-card-text class="pa-5">
        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          class="mb-4"
        >
          {{ error }}
        </v-alert>

        <v-alert
          v-if="localError"
          type="error"
          variant="tonal"
          class="mb-4"
        >
          {{ localError }}
        </v-alert>

        <v-alert
          type="info"
          variant="tonal"
          density="comfortable"
          class="mb-5"
        >
          Select the session currency and enter
          the opening cash balance before starting.
        </v-alert>

        <v-form
          ref="formRef"
          @submit.prevent="submit"
        >
          <!-- TERMINAL -->

          <v-text-field
            :model-value="terminalCode"
            label="Terminal"
            prepend-inner-icon="mdi-point-of-sale"
            variant="outlined"
            readonly
            class="mb-3"
          />

          <!-- BUSINESS DATE -->

          <v-text-field
            :model-value="computerDate"
            label="Computer Business Date"
            prepend-inner-icon="mdi-calendar-today"
            variant="outlined"
            readonly
            class="mb-3"
          />

          <!-- COMPUTER TIMEZONE -->

          <v-text-field
            :model-value="computerTimeZone"
            label="Computer Timezone"
            prepend-inner-icon="mdi-clock-outline"
            variant="outlined"
            readonly
            class="mb-3"
          />

          <!-- CURRENCY -->

          <v-select
            v-model="selectedCurrency"
            :items="currencyOptions"
            item-title="title"
            item-value="value"
            label="Session Currency"
            prepend-inner-icon="mdi-cash-multiple"
            variant="outlined"
            :rules="[requiredCurrency]"
            :disabled="loading"
            class="mb-3"
          />

          <!-- OPENING CASH -->

          <v-text-field
            v-model.number="openingCashAmount"
            label="Opening Cash Amount"
            prepend-inner-icon="mdi-cash"
            type="number"
            min="0"
            step="any"
            variant="outlined"
            :rules="[nonNegativeAmount]"
            :disabled="loading"
            :suffix="selectedCurrency"
            hint="Enter 0 if the cash drawer is empty."
            persistent-hint
            class="mb-3"
          />

          <!-- NOTES -->

          <v-textarea
            v-model="notes"
            label="Opening Notes"
            prepend-inner-icon="mdi-note-text-outline"
            variant="outlined"
            rows="3"
            auto-grow
            maxlength="1000"
            counter
            :disabled="loading"
            class="mt-4"
          />
        </v-form>
      </v-card-text>

      <v-divider />

      <!-- =====================================================
           ACTIONS
      ====================================================== -->

      <v-card-actions class="pa-5">
        <v-btn
          variant="outlined"
          :disabled="loading"
          @click="close"
        >
          Cancel
        </v-btn>

        <v-spacer />

        <v-btn
          color="success"
          prepend-icon="mdi-play-circle-outline"
          :loading="loading"
          :disabled="
            !terminalId ||
            !selectedCurrency ||
            !computerTimeZone ||
            loading
          "
          @click="submit"
        >
          Start Session
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import {
  computed,
  ref,
  watch
} from "vue";

// ============================================================
// PROPS
// ============================================================

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },

  terminal: {
    type: Object,
    default: null
  },

  loading: {
    type: Boolean,
    default: false
  },

  error: {
    type: String,
    default: ""
  },

  defaultCurrency: {
    type: String,
    default: ""
  },

  currencies: {
    type: Array,
    default: () => []
  }
});

// ============================================================
// EVENTS
// ============================================================

const emit = defineEmits([
  "update:modelValue",
  "start"
]);

// ============================================================
// STATE
// ============================================================

const formRef = ref(null);

const openingCashAmount = ref(0);

const selectedCurrency = ref(null);

const notes = ref("");

const localError = ref("");

const computerDate = ref("");

const computerTimeZone = ref("");

// ============================================================
// TERMINAL
// ============================================================

const terminalId = computed(() =>
  props.terminal?.id ??
  props.terminal?.terminalId ??
  null
);

const terminalCode = computed(() =>
  props.terminal?.terminalCode ??
  "POS Terminal"
);

// ============================================================
// CURRENCIES
// ============================================================

const currencyOptions = computed(() =>
  props.currencies
    .map(item => {
      if (typeof item === "string") {
        return {
          title: item.toUpperCase(),
          value: item.toUpperCase()
        };
      }

      const code = String(
        item.value ??
        item.code ??
        item.currencyCode ??
        ""
      ).trim().toUpperCase();

      return {
        title:
          item.title ??
          item.name ??
          code,
        value: code
      };
    })
    .filter(item => item.value)
);

// ============================================================
// COMPUTER DATE AND TIMEZONE
// ============================================================

function getComputerTimeZone() {
  return (
    Intl.DateTimeFormat()
      .resolvedOptions()
      .timeZone || ""
  );
}

function getComputerDate() {
  const now = new Date();

  const year = now.getFullYear();

  const month = String(
    now.getMonth() + 1
  ).padStart(2, "0");

  const day = String(
    now.getDate()
  ).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function refreshComputerDateTime() {
  computerTimeZone.value =
    getComputerTimeZone();

  computerDate.value =
    getComputerDate();
}

// ============================================================
// VALIDATION
// ============================================================

const requiredCurrency = value =>
  Boolean(value) ||
  "Select a session currency.";

const nonNegativeAmount = value => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "Opening cash amount is required.";
  }

  const amount = Number(value);

  return (
    Number.isFinite(amount) &&
    amount >= 0
  ) || "Opening cash cannot be negative.";
};

// ============================================================
// SUBMIT
// ============================================================

async function submit() {
  if (props.loading) {
    return;
  }

  localError.value = "";

  // Refresh immediately before opening the session.
  // This avoids using an outdated date after midnight.

  refreshComputerDateTime();

  if (!computerTimeZone.value) {
    localError.value =
      "Unable to determine the POS computer timezone.";

    return;
  }

  const validation =
    await formRef.value?.validate();

  if (validation && !validation.valid) {
    return;
  }

  if (!terminalId.value) {
    localError.value =
      "A POS terminal must be selected.";

    return;
  }

  if (!selectedCurrency.value) {
    localError.value =
      "Select a session currency.";

    return;
  }

  const amount =
    Number(openingCashAmount.value);

  if (
    !Number.isFinite(amount) ||
    amount < 0
  ) {
    localError.value =
      "Opening cash cannot be negative.";

    return;
  }

  // The parent component receives this complete payload.
  // The backend uses timeZone to determine the business date.

  emit("start", {
    terminalId: terminalId.value,

    currencyCode:
      selectedCurrency.value.toUpperCase(),

    openingCashAmount: amount,

    notes:
      notes.value?.trim() || null,

    timeZone:
      computerTimeZone.value
  });
}

// ============================================================
// CLOSE
// ============================================================

function close() {
  if (props.loading) {
    return;
  }

  localError.value = "";

  emit("update:modelValue", false);
}

// ============================================================
// RESET
// ============================================================

function reset() {
  openingCashAmount.value = 0;

  notes.value = "";

  localError.value = "";

  refreshComputerDateTime();

  const preferredCurrency = String(
    props.defaultCurrency ?? ""
  ).trim().toUpperCase();

  const available = currencyOptions.value.some(
    item => item.value === preferredCurrency
  );

  selectedCurrency.value = available
    ? preferredCurrency
    : null;

  formRef.value?.resetValidation();
}

// ============================================================
// WATCH
// ============================================================

watch(
  () => props.modelValue,
  opened => {
    if (opened) {
      reset();
    }
  }
);
</script>
